import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api, ApiError } from "../../shared/api/client";
import { Button, Drawer, Field, InlineAlert, Input, Select } from "../../shared/ui";
import type { EmployeeSummary, PageResponse, WorkItemDetail } from "../../shared/types/api";

/**
 * Who is testing this, and by when.
 *
 * <p>Deliberately small — a tester and a due date, nothing else. Both are what Start testing
 * refuses to move without, so this is the one panel that satisfies it.
 */
export function TestingDrawer({
  item,
  onClose,
  onSaved,
}: {
  item: WorkItemDetail | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [testerId, setTesterId] = useState("");
  const [testingDueDate, setTestingDueDate] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const employees = useQuery({
    queryKey: ["employees", "picker"],
    queryFn: () =>
      api.get<PageResponse<EmployeeSummary>>(
        "/api/v1/employees?active=true&size=200&sort=fullName,asc",
      ),
    enabled: Boolean(item),
  });

  useEffect(() => {
    if (item) {
      setTesterId(item.testerId ?? "");
      setTestingDueDate(item.testingDueDate ?? "");
      setError(null);
    }
  }, [item]);

  const save = async () => {
    if (!item) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.put(`/api/v1/work-items/${item.id}/testing`, {
        testerId: testerId || null,
        testingDueDate: testingDueDate || null,
      });
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not save testing.");
    } finally {
      setSaving(false);
    }
  };

  const people = employees.data?.content ?? [];

  return (
    <Drawer
      open={Boolean(item)}
      onClose={onClose}
      title="Testing"
      subtitle={item ? `${item.itemKey} — ${item.title}` : undefined}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button onClick={() => void save()} loading={saving}>
            {saving ? "Saving…" : "Save testing"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        <Field label="Tester" hint="Who is testing this.">
          <Select value={testerId} onChange={(event) => setTesterId(event.target.value)}>
            <option value="">Unassigned</option>
            {people.map((employee) => (
              <option key={employee.id} value={employee.id}>
                {employee.fullName} — {employee.designation}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Testing due">
          <Input
            type="date"
            value={testingDueDate}
            onChange={(event) => setTestingDueDate(event.target.value)}
          />
        </Field>
      </div>
    </Drawer>
  );
}
